import React, { useState, useEffect, useRef } from 'react';
import {
  Container,
  Header,
  SpaceBetween,
  Button,
  Badge,
  Spinner,
  Alert,
  Toggle
} from '@cloudscape-design/components';
import Grid from "@cloudscape-design/components/grid";

import TaskCreateModal from './TaskCreateModal';
import TaskEditModal from './TaskEditModal';
import AvatarUpload from './AvatarUpload';
import ClassroomMessages from './ClassroomMessages';
import { API_BASE_URL } from '../config/api';
import axios from 'axios';

const TaskView = ({ familyId, familyName, currentUser, initialMembers = [], chatrooms = [] }) => {
  const [tasks, setTasks] = useState([]);
  const [familyMembers, setFamilyMembers] = useState([]);
  const [memberPoints, setMemberPoints] = useState({});
  const [filter, setFilter] = useState('all');
  const [memberTasks, setMemberTasks] = useState({});
  const [isTasksLoading, setIsTasksLoading] = useState(false);
  const [error, setError] = useState(null);
  const [showChildrenOnly, setShowChildrenOnly] = useState(true);
  const [refreshTrigger, setRefreshTrigger] = useState(0); // 用于触发刷新

  // 对话框状态
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [selectedMember, setSelectedMember] = useState(null);
  const [showEditModal, setShowEditModal] = useState(false);
  const [editingTask, setEditingTask] = useState(null);
  const [showAvatarModal, setShowAvatarModal] = useState(false);
  const [selectedAvatarMember, setSelectedAvatarMember] = useState(null);
  const [memberAvatars, setMemberAvatars] = useState({});
  const [visible, setVisible] = useState(false);

  // 用于防止重复加载任务
  const hasLoadedTasksRef = useRef(false);
  const refreshIntervalRef = useRef(null);
  /** 获取成员角色 */
  const getMemberRole = (member) => {
    const roleField = member.role || member.role_type || member.user_role;
    if (roleField) {
      return ['Parent', 'parent'].includes(roleField) ? 'parent' : 'child';
    }
    if (['爸', '妈', '爸爸', '妈妈'].includes(member.name)) return 'parent';
    return 'child';
  };

  /** 获取家庭成员金币信息 */
  const fetchFamilyPoints = async () => {
    try {
      const response = await axios.get(`${API_BASE_URL}/families/${familyId}/points`);
      setMemberPoints(response.data || {});
      return response.data || {};
    } catch (error) {
      console.error('❌ 获取家庭成员金币失败:', error);
      // 设置默认金币数据
      const defaultPoints = {};
      familyMembers.forEach(member => {
        defaultPoints[member.id] = {
          name: member.name,
          available: 0,
          exchanged: 0
        };
      });
      setMemberPoints(defaultPoints);
      return defaultPoints;
    }
  };

  /** 获取任务 */
  const fetchFamilyTasks = async (dateFilter = 'today') => {
    try {
      let date;
      if (dateFilter === 'yesterday') {
        // 获取昨天的日期
        const yesterday = new Date();
        yesterday.setDate(yesterday.getDate() - 1);
        date = yesterday.toISOString().split('T')[0];
      } else {
        // 默认获取今天的日期
        date = new Date().toISOString().split('T')[0];
      }

      const response = await axios.get(`${API_BASE_URL}/tasks/?family_id=${familyId}&date=${date}`);
      const tasksData = (response.data || []).map(task => ({
        ...task,
        id: task._id || task.id,
        duration:
          task.duration ||
          Math.floor((new Date(task.end_time) - new Date(task.start_time)) / (1000 * 60)) ||
          30
      }));
      setTasks(tasksData);
      return tasksData;
    } catch (error) {
      console.error('❌ 获取任务失败:', error);
      throw new Error('获取任务失败');
    }
  };

  /** 初始化成员数据 - 直接使用 props 传入的 initialMembers */
  useEffect(() => {
    if (initialMembers && initialMembers.length > 0) {
      const members = initialMembers.map((member, index) => ({
        ...member,
        id: member.id || member._id || String(index + 1),
        avatar: member.avatar || (getMemberRole(member) === 'parent' ? '👨' : '👦')
      }));
      setFamilyMembers(members);
    }
  }, [initialMembers]);

  /** 手动刷新函数 - 供子组件调用 */
  const handleRefresh = async () => {
    setRefreshTrigger(prev => prev + 1);
  };

  /** 成员加载完成后再加载任务和金币 - 只请求一次 */
  useEffect(() => {
    const loadTasksAndPoints = async () => {
      if (!familyId || familyMembers.length === 0) {
        return;
      }

      // 防止重复加载（仅当 filter 变化时才重新加载）
      const currentKey = `${familyId}_${filter}_${refreshTrigger}`;
      if (hasLoadedTasksRef.current === currentKey) {
        return;
      }

      hasLoadedTasksRef.current = currentKey;
      setIsTasksLoading(true);
      try {
        await fetchFamilyTasks(filter === 'yesterday' ? 'yesterday' : 'today');
        await fetchFamilyPoints();
      } catch (error) {
        console.error('加载任务数据失败:', error);
        hasLoadedTasksRef.current = null; // 失败后允许重试
      } finally {
        setIsTasksLoading(false);
      }
    };
    loadTasksAndPoints();
  }, [familyId, filter, familyMembers.length, refreshTrigger]);

  /** 设置自动刷新定时器 - 每5分钟刷新一次 */
  useEffect(() => {
    // 清除旧的定时器
    if (refreshIntervalRef.current) {
      clearInterval(refreshIntervalRef.current);
    }

    // 设置新的定时器 - 5分钟(300000毫秒)
    refreshIntervalRef.current = setInterval(() => {
      console.log('自动刷新任务和消息...');
      setRefreshTrigger(prev => prev + 1);
    }, 300000); // 5分钟

    // 组件卸载时清除定时器
    return () => {
      if (refreshIntervalRef.current) {
        clearInterval(refreshIntervalRef.current);
      }
    };
  }, []);

  /** 按成员分组任务 */
  useEffect(() => {
    if (!familyMembers.length) return;
    const grouped = {};
    familyMembers.forEach(member => {
      grouped[member.id] = tasks.filter(task => {
        const matchesMember =
          task.member_id === member.id ||
          task.member_id === member._id ||
          task.member_name === member.name;
        if (!matchesMember) return false;

        if (filter === 'today') {
          const today = new Date().toDateString();
          return new Date(task.start_time).toDateString() === today;
        }
        if (filter === 'yesterday') {
          const yesterday = new Date();
          yesterday.setDate(yesterday.getDate() - 1);
          return new Date(task.start_time).toDateString() === yesterday.toDateString();
        }
        if (filter === 'completed') return task.status === 'completed';
        if (filter === 'pending') return task.status !== 'completed';
        return true;
      });
    });
    setMemberTasks(grouped);
  }, [tasks, familyMembers, filter]);

  /** 操作函数 */
  const handleCompleteTask = async (taskId) => {
    try {
      // 找到要完成的任务
      const task = tasks.find(t => t.id === taskId);
      if (!task) {
        console.error('任务不存在');
        return;
      }

      // 调用完成任务的API，传递任务执行者的ID
      const userId = task.member_id || currentUser?.id;
      const response = await axios.post(`${API_BASE_URL}/tasks/${taskId}/complete`, userId);

      // 更新任务状态
      setTasks(prev => prev.map(t =>
        t.id === taskId ? { ...t, status: 'completed', ...response.data } : t
      ));

      // 如果任务有奖励金币，更新成员的金币余额
      if (task.points && task.points > 0 && task.member_id) {
        await fetchFamilyPoints(); // 重新获取金币信息
      }
    } catch (error) {
      console.error('完成任务失败:', error);
      // 至少更新任务状态，即使API调用失败
      setTasks(prev => prev.map(task =>
        task.id === taskId ? { ...task, status: 'completed' } : task
      ));
    }
  };

  const handleDeleteTask = async (taskId) => {
    try {
      await axios.delete(`${API_BASE_URL}/tasks/${taskId}/`);
      setTasks(prev => prev.filter(task => task.id !== taskId));
    } catch {
      setTasks(prev => prev.filter(task => task.id !== taskId));
    }
  };

  const handleTaskUpdate = async (updatedTask) => {
    try {
      const response = await axios.put(`${API_BASE_URL}/tasks/${updatedTask.id}`, updatedTask);
      setTasks(prev => prev.map(task =>
        task.id === updatedTask.id ? { ...task, ...response.data } : task
      ));
    } catch {
      setTasks(prev => prev.map(task =>
        task.id === updatedTask.id ? { ...task, ...updatedTask } : task
      ));
    }
  };

  const handleTaskSubmit = async (taskData) => {
    try {
      const response = await axios.post(`${API_BASE_URL}/tasks/`, {
        ...taskData, family_id: familyId, status: 'pending'
      });
      const newTask = { ...response.data, id: response.data._id || response.data.id };
      setTasks(prev => [...prev, newTask]);
    } catch {
      const tempTask = {
        ...taskData,
        id: `temp_${Date.now()}`,
        family_id: familyId,
        status: 'pending',
        created_at: new Date().toISOString()
      };
      setTasks(prev => [...prev, tempTask]);
    }
  };

  /** 工具函数 */
  const getTaskTypeColor = (type) =>
    ({ task: 'blue', plan: 'green', note: 'orange' }[type] || 'grey');

  const getTaskTypeLabel = (type) =>
    ({ task: '任务', plan: '计划', note: '笔记' }[type] || '其他');

  const formatTime = (timeString) =>
    new Date(timeString).toLocaleTimeString('zh-CN', { hour: '2-digit', minute: '2-digit' });

  const formatDate = (timeString) =>
    new Date(timeString).toLocaleDateString('zh-CN', {
      month: '2-digit', day: '2-digit', weekday: 'short'
    });

  // 提取顶部菜单为可复用组件
  const headerComponent = (
    <Header
      variant="h1"
      description={`${familyId ? `家庭ID: ${familyId}` + ' | ' : ''}共 ${familyMembers.length} 位成员 | ${tasks.length} 个任务`}
      actions={
        <SpaceBetween size="s" direction="horizontal">
          <Toggle
            key={"toggle-children"}
            checked={showChildrenOnly}
            onChange={({ detail }) => setShowChildrenOnly(detail.checked)}
          >
            只显示孩子任务
          </Toggle>
          <Button key={"filter-all"} variant={filter === 'all' ? 'primary' : 'normal'} onClick={() => setFilter('all')}>全部任务</Button>
          <Button key={"filter-yesterday"} variant={filter === 'yesterday' ? 'primary' : 'normal'} onClick={() => setFilter('yesterday')}>昨日任务</Button>
          <Button key={"filter-today"} variant={filter === 'today' ? 'primary' : 'normal'} onClick={() => setFilter('today')}>今日任务</Button>
          <Button key={"filter-pending"} variant={filter === 'pending' ? 'primary' : 'normal'} onClick={() => setFilter('pending')}>待完成</Button>
          <Button key={"filter-completed"} variant={filter === 'completed' ? 'primary' : 'normal'} onClick={() => setFilter('completed')}>已完成</Button>
          <Button key={"create-task"} variant="normal" onClick={() => setShowCreateModal(true)}>创建任务</Button>
        </SpaceBetween>
      }
    >
      {familyName || '家庭任务视图'}
    </Header>
  );

  /** 成员加载状态 - 不再需要，直接使用 props */
  if (!familyMembers || familyMembers.length === 0) {
    return (
      <Container>
        <Alert type="warning" header="没有成员数据">
          正在等待家庭成员数据加载...
        </Alert>
      </Container>
    );
  }

  if (error) {
    return (
      <Container>
        <Alert type="error" header="加载失败">
          {error}
          <div style={{ marginTop: '1rem' }}>
            <Button onClick={() => window.location.reload()}>刷新页面</Button>
          </div>
        </Alert>
      </Container>
    );
  }

  // 过滤要显示的成员
  const displayMembers = familyMembers.filter(m => !showChildrenOnly || getMemberRole(m) === 'child');

  if (displayMembers.length === 0) {
    return (
      <Container header={headerComponent}>
        <Alert type="warning" header="没有成员">
          当前家庭没有成员信息，请先添加家庭成员。
        </Alert>
      </Container>
    );
  }

  return (
    <>
      <Grid
        gridDefinition={[{ colspan: 5 }, { colspan: 7 }]}
      >
          <ClassroomMessages chatrooms={chatrooms} onRefresh={handleRefresh} refreshTrigger={refreshTrigger} />
          <Container header={headerComponent}>
            {/* 家庭成员列视图 - 始终显示成员容器 */}
            <div
              style={{
                display: 'grid',
                gridTemplateColumns: `repeat(${Math.min(displayMembers.length, 4)}, 1fr)`,
                gap: '1.5rem',
                width: '100%'
              }}
            >
              {displayMembers.map((member) => {
                const memberTaskList = memberTasks[member.id] || [];

                return (
                  <Container
                    key={member.id}
                    header={
                      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', width: '100%' }}>
                        {/* 左侧：头像和信息 */}
                        <div style={{ display: 'flex', alignItems: 'center', gap: '1rem' }}>
                          {/* 头像 */}
                          <div
                            style={{ cursor: 'pointer', flexShrink: 0 }}
                            onClick={() => {
                              setSelectedAvatarMember(member);
                              setShowAvatarModal(true);
                            }}
                            title="点击更换头像"
                          >
                            {memberAvatars[member.id] ? (
                              <img src={memberAvatars[member.id]} alt={member.name}
                                style={{ width: '50px', height: '50px', borderRadius: '50%' }} />
                            ) : member.avatar?.startsWith('data:image/') ? (
                              <img src={member.avatar} alt={member.name}
                                style={{ width: '50px', height: '50px', borderRadius: '50%' }} />
                            ) : (
                              <div style={{ fontSize: '2.5rem' }}>{member.avatar || '👤'}</div>
                            )}
                          </div>

                          {/* 信息区域 */}
                          <div style={{ display: 'flex', alignItems: 'center', gap: '0.75rem' }}>
                            <span style={{ fontSize: '1.3rem', fontWeight: 'bold' }}>{member.name}</span>
                            <Badge color={getMemberRole(member) === 'parent' ? 'blue' : 'green'}>
                              {getMemberRole(member) === 'parent' ? '家长' : '孩子'}
                            </Badge>

                            {/* 显示金币信息 - 只对孩子显示 */}
                            {getMemberRole(member) === 'child' && (
                              <div style={{
                                display: 'flex',
                                alignItems: 'center',
                                gap: '0.3rem',
                                padding: '0.3rem 0.6rem',
                                backgroundColor: '#fff7e6',
                                borderRadius: '4px',
                                border: '1px solid #ffd591'
                              }}>
                                <span style={{ fontSize: '1.1rem' }}>🪙</span>
                                <span style={{ fontWeight: 'bold', color: '#ff9900', fontSize: '1.1rem' }}>
                                  {memberPoints[member.id]?.available || 0}
                                </span>
                              </div>
                            )}

                            <span style={{ fontSize: '0.9rem', color: 'var(--color-text-body-secondary)' }}>
                              共 {memberTaskList.length} 个任务
                            </span>
                          </div>
                        </div>

                        {/* 右侧：添加按钮 */}
                        <Button
                          variant="primary"
                          iconName="add-plus"
                          onClick={(e) => {
                            e.stopPropagation();
                            setSelectedMember(member);
                            setShowCreateModal(true);
                          }}
                        />
                      </div>
                    }
                  >
                    {/* 任务列表区域 */}
                    {isTasksLoading ? (
                      <div style={{ textAlign: 'center', padding: '2rem' }}>
                        <Spinner size="normal" />
                        <div style={{ marginTop: '0.5rem', fontSize: '0.9rem', color: 'var(--color-text-body-secondary)' }}>
                          加载任务中...
                        </div>
                      </div>
                    ) : memberTaskList.length === 0 ? (
                      <div style={{ textAlign: 'center', padding: '2rem', color: 'var(--color-text-body-secondary)' }}>
                        暂无任务
                      </div>
                    ) : (
                      <SpaceBetween size="s">
                        {memberTaskList.map((task) => (
                          <div key={task.id} style={{
                            padding: '1rem',
                            border: '1px solid var(--color-border-divider-default)',
                            borderRadius: '8px',
                            backgroundColor:
                              task.status === 'completed'
                                ? 'var(--color-background-status-success)'
                                : 'var(--color-background-container-content)'
                          }}>
                            <div style={{ display: 'flex', gap: '0.5rem', alignItems: 'center', marginBottom: '0.5rem' }}>
                              <Badge color={getTaskTypeColor(task.type)}>{getTaskTypeLabel(task.type)}</Badge>
                              {task.points && <Badge color="blue">+{task.points}🪙</Badge>}
                              {task.status === 'completed' && <Badge color="green">已完成</Badge>}
                            </div>
                            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
                              <div style={{ fontSize: '1.1rem', fontWeight: 'bold' }}>{task.title || '无标题'}</div>
                              <SpaceBetween size="xs" direction="horizontal">
                                <Button key="edit" variant="icon" iconName="edit" onClick={() => setEditingTask(task) || setShowEditModal(true)} />
                                {task.status !== 'completed' && (
                                  <Button key="complete" variant="icon" iconName="status-positive" onClick={() => handleCompleteTask(task.id)} />
                                )}
                                <Button key="delete" variant="icon" iconName="remove" onClick={() => handleDeleteTask(task.id)} />
                              </SpaceBetween>
                            </div>
                            {task.description && (
                              <div style={{ fontSize: '0.9rem', color: 'var(--color-text-body-secondary)' }}>{task.description}</div>
                            )}
                            {task.start_time && (
                              <div style={{ fontSize: '0.8rem', color: 'var(--color-text-body-secondary)' }}>
                                {formatDate(task.start_time)} {formatTime(task.start_time)}
                              </div>
                            )}
                          </div>
                        ))}
                      </SpaceBetween>
                    )}
                  </Container>
                );
              })}
            </div>

            {/* 全局提示信息 - 仅当完全没有任务时显示 */}
            {tasks.length === 0 && (
              <div style={{ textAlign: 'center', marginTop: '2rem' }}>
                <Alert type="info" header="暂无任务">
                  {filter === 'yesterday' ? '昨日没有任务记录，您可以切换到其他视图查看任务。' : '当前没有任务，点击任意成员的"添加"按钮创建新任务。'}
                </Alert>
              </div>
            )}
          </Container>
        {/* 对话框组件 */}
        <TaskCreateModal
          visible={showCreateModal}
          onDismiss={() => {
            setShowCreateModal(false);
          }}
          onSubmit={handleTaskSubmit}
          member={selectedMember}
          currentUser={currentUser}
        />
        <TaskEditModal
          visible={showEditModal}
          onDismiss={() => setShowEditModal(false)}
          onSave={handleTaskUpdate}
          task={editingTask}
          familyMembers={familyMembers}
        />
        <AvatarUpload
          visible={showAvatarModal}
          onDismiss={() => setShowAvatarModal(false)}
          member={selectedAvatarMember}
          familyId={familyId}
          onSuccess={(memberId, newAvatar) =>
            setMemberAvatars(prev => ({ ...prev, [memberId]: newAvatar }))
          }
        />
      </Grid>
    </>

  );
};

export default TaskView;
